/**
 * The catalogue of clearable formations, and what they are worth.
 *
 * Two families, both confined to a single face:
 *
 *   Bars      3, 5 or 7 cells in a continuous line spanning the full width of a
 *             face along one of the three axes — its level rows and the
 *             diagonals parallel to each sloped edge. 9 per face, 27 in all.
 *
 *   Pyramids  4 or 9 cells forming a larger triangle: the 4x Pyramid and the
 *             9x Great Pyramid. 7 and 3 per face respectively (only one 4x per
 *             face points downward, and no downward 9x fits a 4-row face).
 *
 * A formation must be completely filled and clears as a whole. Nothing else
 * clears: a run that merely happens to be three long is not a bar unless it
 * spans the face.
 */

import { CELLS, faceRows, faceTriangles } from './geometry.js';

/**
 * Points per formation. Growth is steeply super-linear in cells, so holding out
 * for a bigger shape beats taking the small one: a 9x Great Pyramid is worth
 * more than four 4x Pyramids, and a 7x Bar more than six 3x Bars.
 */
export const FORMATION_SCORE = {
  bar3: 100,
  bar5: 350,
  bar7: 800,
  pyramid4: 250,
  pyramid9: 1600,
};

/**
 * Multiplier for completing several formations with one piece.
 *
 * `n + (n - 1)^2 / 2` — worth at least n times a single, and pulling further
 * ahead the more you land at once, so a double always beats two separate
 * singles. Unbounded, because there is no natural ceiling on how many
 * formations one well-placed piece can close.
 *
 *   1x -> 1     2x -> 2.5    3x -> 5    4x -> 8.5    5x -> 13
 */
export function comboMultiplier(count) {
  if (count <= 1) return 1;
  return count + (count - 1) ** 2 / 2;
}

const BAR_KIND = { 3: 'bar3', 5: 'bar5', 7: 'bar7' };

/**
 * @type {{cells: number[], size: number, kind: string, family: 'bar'|'pyramid',
 *         label: string, face: number, score: number}[]}
 */
export const FORMATIONS = [
  ...faceRows(3).map(({ face, cells }) => ({
    cells,
    size: cells.length,
    kind: BAR_KIND[cells.length],
    family: 'bar',
    label: `${cells.length}x Bar`,
    face,
  })),
  ...faceTriangles(2).map(({ face, cells }) => ({
    cells,
    size: 4,
    kind: 'pyramid4',
    family: 'pyramid',
    label: '4x Pyramid',
    face,
  })),
  ...faceTriangles(3).map(({ face, cells }) => ({
    cells,
    size: 9,
    kind: 'pyramid9',
    family: 'pyramid',
    label: '9x Great Pyramid',
    face,
  })),
].map((formation) => ({ ...formation, score: FORMATION_SCORE[formation.kind] }));

/** formationsByCell[cellId] -> indices into FORMATIONS. */
export const FORMATIONS_BY_CELL = CELLS.map(() => []);
FORMATIONS.forEach((formation, index) => {
  for (const id of formation.cells) FORMATIONS_BY_CELL[id].push(index);
});

/** Formations whose every cell is filled. */
export function findClears(filled) {
  return FORMATIONS.filter((f) => f.cells.every((id) => filled.has(id)));
}

/**
 * Formations that `candidate` cells would complete on top of what is already
 * filled — used to light up an incoming clear during the drop preview.
 */
export function formationsCompletedBy(filled, candidate) {
  const after = new Set(filled);
  for (const id of candidate) after.add(id);

  const touched = new Set();
  for (const id of candidate) {
    for (const index of FORMATIONS_BY_CELL[id]) touched.add(index);
  }

  const out = [];
  for (const index of touched) {
    const formation = FORMATIONS[index];
    if (formation.cells.every((id) => after.has(id))) out.push(formation);
  }
  return out;
}

/**
 * Score a set of simultaneously completed formations.
 * @returns {{points: number, multiplier: number, base: number}}
 */
export function scoreClears(clears) {
  const base = clears.reduce((sum, f) => sum + f.score, 0);
  const multiplier = comboMultiplier(clears.length);
  return { base, multiplier, points: Math.round(base * multiplier) };
}
